import { createApp } from 'vue'
import { createSMWRouter } from './__core__/router'
import { CDN } from './__core__/cdn/cdn'
import { WidgetMQ, WidgetMQEvents } from './__core__/WidgetMQ/WidgetMQ'
import { initFeatureToggleDirective } from './__core__/directives/feature-toggle'
import LocalizationPlugin from './__core__/localization/plugin'
import { createPinia } from 'pinia'
import { featureToggleManager } from '@/__core__/feature-toggle/feature-toggle-manager'

// const isFeatureActive = feature => featureToggleManager.isFeatureActive(feature)

/**
 * @property { string } containerId is id of a parent container (div), where widget will be rendered
 * @property { string } widgetId is id of a specific widget type (lineup, statistics, standings, etc.)
 * @property { object } payload is an object containing all necessary payload for a secific vendor, depdending on specoific widget
 */
const WidgetPayload = {
  containerId: '',
  widgetId: '',
  payload: {}
}

window.createWidget = async ({ containerId = 'sports-mode-root', widgetId = 'app', payload = {}, routerMode } = WidgetPayload, renderingMode = 'widget') => {
  try {
    if (!_isWidgetModeValid(renderingMode)) {
      throw new Error(`Rendering mode: ${renderingMode} is not allowed! It should be either "app" or "widget"`)
    }

    /* update current cdn url, based on the renderingMode, and script-id attribute */
    await CDN.updateCdnUrl(renderingMode)
    await featureToggleManager.loadFeatures(CDN.getCdnUrl())

    /* Render sports mode as specific widget, for instance Statistics or Lineup widget, based on widgetPayload.widgetId */
    const lazyLoadWidget = {
      /* Render sports mode as app, collection of widgets */
      app: async () => import('./SportsModeWidget.vue'),
      statistics: async () => import('./__v1.5__/widgets/match-summary/MatchSummary.vue'),
      statisticsKeyMoments: async () => import('./__v1.5__/widgets/match-summary/MatchSummary.vue'),
      statisticsGameStats: async () => import('./__v1.5__/widgets/match-summary/MatchSummary.vue'),
      scoreboard: async () => import(`./widgets/scoreboard/ScoreboardWidget.vue`),
      standings: async () => import('./__v1.5__/widgets/standings/Standings.vue'),
      leaderboard: async () => import('./__v1.5__/widgets/leaderboard/Leaderboard.vue'),
      fixtures: async () => import('./__v1.5__/widgets/fixtures/Fixtures.vue'),
      playerProfile: async () => import('./__v1.5__/widgets/playerProfile/PlayerProfile.vue'),
      // playerProfile: async () => import('./widgets/playerProfile/PlayerProfileWidget.vue'),
      teamProfile: async () => import('./__v1.5__/widgets/teamProfile/TeamProfile.vue'),
      teamsLineup: async () => import('./widgets/teamsLineup/TeamsLineupWidget.vue'),
      singleLineup: async () => import('./widgets/teamsLineup/TeamsLineupWidget.vue'),
      eventsStripeV2: async () => import('./widgets/stripe/Stripe.vue'),
      eventsStripe: async () => import('./widgets/stripe-legacy/Stripe.vue'),
      eventsStripeEuro: async () => import('./widgets/stripe-legacy/Stripe.vue'),
      eventsStripeOlympics: async () => import('./widgets/stripe-legacy/Stripe.vue'),
      knockout: async () => import('./widgets/knockout/Knockout.vue'),
      tennisRankings: async () => import('./widgets/rankings/RankingsWidget.vue'),
      euroleaguePlayerProfile: async () => import('./widgets/euroleague/player-profile/PlayerProfile.vue'),
      euroleagueStandings: async () => import('./widgets/standings/BasketballStandings.vue'),
      euroleagueRoster: async () => import('./widgets/euroleague/roster/Roster.vue'),
      euroleagueBoxscore: async () => import('./widgets/euroleague/boxscore/Boxscore.vue'),
      euroleagueStatistics: async () => import('./widgets/euroleague/statistics/Statistics.vue'),
      euroleagueFixtures: async () => import('./widgets/euroleague/fixtures/FixturesWidget.vue'),
      euroleagueTeamProfile: async () => import('./widgets/euroleague/team-profile/TeamProfile.vue'),
      euroleaguePlayerLeaderboard: async () => import('./widgets/euroleague/leaderboard/Leaderboard.vue'),
      euroleagueTeamLeaderboard: async () => import('./widgets/euroleague/leaderboard/Leaderboard.vue'),
      euroleaguePlayByPlay: async () => import('./widgets/euroleague/play-by-play/PlayByPlay.vue'),
      basketballStatistics: async () => import('./widgets/statistics/BasketballStatistics.vue'),
      basketballBoxscore: async () => import('./widgets/boxscore/BasketballBoxscoreWidget.vue'),
      basketballPlayerProfile: async () => import('./widgets/playerProfile/BasketballPlayerProfile.vue'),
      basketballStandings: async () => import('./widgets/standings/BasketballStandings.vue'),
      basketballTeamProfile: async () => import('./widgets/teamProfile/BasketballTeamProfile.vue'),
      basketballFixtures: async () => import('./widgets/fixtures/BasketballFixtures.vue'),
      basketballRoster: async () => import('./widgets/roster/BasketballRoster.vue'),
      basketballScoreboard: async () => import('./widgets/scoreboard/Basketball.vue'),
      basketballKnockout: async () => import('./widgets/knockout/BasketballKnockout.vue'),
      liveScore: async () => import('./widgets/livescore/LiveScoreboard.widget.vue'),
      matchCommentary: async () => import('./widgets/match-commentary/MatchCommentary.widget.vue'),
      tennisPlayerProfile: async () => import('./widgets/playerProfile/TennisPlayerProfile.vue'),
      tennisStatistics: async () => import('./widgets/tennis-statistics/Statistics.vue'),
      tennisDraw: async () => import('./widgets/knockout/TennisKnockout.vue'),
      tennisFixtures: async () => import('./widgets/fixtures/TennisFixtures.vue')
    }[widgetId]

    if (!widgetId || !lazyLoadWidget) {
      throw new Error(`There is no widget with id: ${widgetId}`)
    }

    if (!containerId || !_doesContainerWithIdExist(containerId)) {
      throw new Error(`There is no container with id: ${containerId}! Note: container must be a div element!`)
    }

    const { default: Widget } = await lazyLoadWidget()
    _createApp({ Widget, containerId, routerMode, rootProps: payload })

    return {
      WidgetMQ,
      WidgetMQEvents
    }
  } catch (error) {
    console.log('Error occurred during widget creation!', error)
  }
}

const _createApp = ({ Widget, containerId, rootProps = {}, options = {}, routerMode = '' }) => {
  const router = createSMWRouter(routerMode)
  const pinia = createPinia()

  createApp({ ...Widget, ...options }, rootProps)
    .directive('ft', initFeatureToggleDirective())
    .use(router)
    .use(pinia)
    .use(LocalizationPlugin)
    .mount(`#${containerId}`)
}

const _doesContainerWithIdExist = (containerId = '') => {
  const container = document.getElementById(containerId)
  return container?.tagName?.toLowerCase() === 'div'
}

const _isWidgetModeValid = (renderingMode = '') => ['app', 'widget'].includes(renderingMode)

window.wmq = WidgetMQ
window.wmqEvents = WidgetMQEvents
